import React, { useState } from 'react';
import { ManagementTable } from '../../Shared';
import { getAdminMoments, deleteMoment, createMoment, updateMoment, dedupeMoments, MemorableMoment } from '../../../../../services/api';
import { useToast } from '../../../../../hooks/use-toast';
import { Button } from '../../../Button';
import { Camera } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../../../Modal';
import MomentForm from './MomentForm';

const MomentManagement = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMoment, setEditingMoment] = useState<MemorableMoment | undefined>(undefined);
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    const { data: moments, isLoading } = useQuery({
        queryKey: ['admin-moments'],
        queryFn: getAdminMoments,
        staleTime: 1000 * 60 * 5,
    });

    const createMutation = useMutation({
        mutationFn: createMoment,
        onSuccess: (newMoment) => {
            queryClient.setQueryData(['admin-moments'], (old: MemorableMoment[] | undefined) => {
                return old ? [newMoment, ...old] : [newMoment];
            });
            // Invalidate public moments cache to refresh main frontend
            queryClient.invalidateQueries({ queryKey: ['moments'] });
            showToast("Moment created successfully", "success");
            setIsModalOpen(false);
        },
        onError: (error: any) => {
            showToast(error.message || "Failed to create moment", "error");
        }
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, formData }: { id: number, formData: FormData }) => updateMoment(id, formData),
        onSuccess: (updatedMoment) => {
            // Update admin cache
            queryClient.setQueryData(['admin-moments'], (old: MemorableMoment[] | undefined) => {
                return old ? old.map(m => m.id === updatedMoment.id ? updatedMoment : m) : [updatedMoment];
            });
            // Invalidate public moments cache to refresh main frontend
            queryClient.invalidateQueries({ queryKey: ['moments'] });
            showToast("Moment updated successfully", "success");
            setIsModalOpen(false);
            setEditingMoment(undefined);
        },
        onError: (error: any) => {
            showToast(error.message || "Failed to update moment", "error");
        }
    });

    const deleteMutation = useMutation({
        mutationFn: deleteMoment,
        onMutate: async (momentId) => {
            await queryClient.cancelQueries({ queryKey: ['admin-moments'] });
            const previousMoments = queryClient.getQueryData(['admin-moments']);
            queryClient.setQueryData(['admin-moments'], (old: MemorableMoment[] | undefined) => {
                return old ? old.filter(m => m.id !== momentId) : [];
            });
            return { previousMoments };
        },
        onError: (err, momentId, context) => {
            if (context?.previousMoments) {
                queryClient.setQueryData(['admin-moments'], context.previousMoments);
            }
            showToast("Failed to delete moment", "error");
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-moments'] });
        },
        onSuccess: () => {
            // Invalidate public moments cache to refresh main frontend
            queryClient.invalidateQueries({ queryKey: ['moments'] });
            showToast("Moment deleted successfully", "success");
        }
    });

    const dedupeMutation = useMutation({
        mutationFn: dedupeMoments,
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['admin-moments'] });
            queryClient.invalidateQueries({ queryKey: ['moments'] });
            showToast(result.message || `Removed ${result.removed_count} duplicate(s)`, "success");
        },
        onError: (error: any) => {
            showToast(error.message || "Failed to remove duplicates", "error");
        },
    });

    const handleDedupe = () => {
        if (window.confirm('Remove duplicate moments with the same title? The best copy (with image) is kept.')) {
            dedupeMutation.mutate();
        }
    };

    const handleAdd = () => {
        setEditingMoment(undefined);
        setIsModalOpen(true);
    };

    const handleEdit = (moment: MemorableMoment) => {
        setEditingMoment(moment);
        setIsModalOpen(true);
    };

    const handleDelete = async (moment: MemorableMoment) => {
        if (window.confirm(`Are you sure you want to delete "${moment.title}"?`)) {
            deleteMutation.mutate(moment.id);
        }
    };

    const handleSubmit = async (formData: FormData) => {
        if (editingMoment) {
            updateMutation.mutate({ id: editingMoment.id, formData });
        } else {
            createMutation.mutate(formData);
        }
    };

    const columns = [
        {
            header: 'Moment',
            accessor: (item: MemorableMoment) => (
                <div className="flex items-center gap-3">
                    {item.image_url ? (
                        <img src={item.image_url} alt={item.title} className="h-10 w-10 rounded-md object-cover border shadow-sm" />
                    ) : (
                        <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center border">
                            <Camera className="h-4 w-4 text-muted-foreground" />
                        </div>
                    )}
                    <div>
                        <p className="font-medium text-sm">{item.title}</p>
                        <p className="text-[11px] text-muted-foreground line-clamp-1 max-w-[200px]">{item.description}</p>
                    </div>
                </div>
            )
        },
        {
            header: 'Date',
            accessor: (item: MemorableMoment) => (
                <span className="text-xs text-muted-foreground">{item.date}</span>
            )
        }
    ];

    return (
        <>
            <div className="flex justify-end mb-2">
                <Button
                    type="button"
                    variant="outline"
                    onClick={handleDedupe}
                    disabled={dedupeMutation.isPending || isLoading || !(moments?.length)}
                >
                    {dedupeMutation.isPending ? 'Removing duplicates...' : 'Remove duplicates'}
                </Button>
            </div>
            <ManagementTable
                title="Memorable Moments"
                data={moments || []}
                columns={columns}
                onAdd={handleAdd}
                onEdit={handleEdit}
                onDelete={handleDelete}
                isLoading={isLoading}
            />

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editingMoment ? 'Edit Moment' : 'Add New Moment'}
            >
                <MomentForm
                    moment={editingMoment}
                    onSubmit={handleSubmit}
                    onCancel={() => setIsModalOpen(false)}
                    isSubmitting={createMutation.isPending || updateMutation.isPending}
                />
            </Modal>
        </>
    );
};

export default MomentManagement;
